import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Token is stored directly in localStorage by authStore
        const accessToken = localStorage.getItem('accessToken');
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    const response = await axios.post(`${API_URL}/auth/refresh`, {
                        refreshToken
                    });

                    const { accessToken, refreshToken: newRefreshToken } = response.data.data;

                    // Update stored tokens
                    localStorage.setItem('accessToken', accessToken);
                    localStorage.setItem('refreshToken', newRefreshToken);

                    processQueue(null, accessToken);
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('auth-storage');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// ================== AUTH API ==================
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
    getProfile: () => api.get('/auth/me'),
    changePassword: (data) => api.put('/auth/change-password', data),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data)
};

// ================== USER API ==================
export const userApi = {
    list: (params) => api.get('/users', { params }),
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    getPermissions: (id) => api.get(`/users/${id}/permissions`),
    activate: (id) => api.put(`/users/${id}/activate`),
    deactivate: (id) => api.put(`/users/${id}/deactivate`)
};

// ================== DASHBOARD API ==================
export const dashboardApi = {
    getOverview: () => api.get('/dashboard/overview'),
    getPendingActions: () => api.get('/dashboard/pending-actions'),
    getSpendingByCategory: () => api.get('/dashboard/spending-by-category'),
    getSpendingByDepartment: () => api.get('/dashboard/spending-by-department'),
    getMonthlyTrend: () => api.get('/dashboard/monthly-trend'),
    getTopVendors: () => api.get('/dashboard/top-vendors'),
    getBudgetUtilization: () => api.get('/dashboard/budget-utilization'),
    getRFQStatistics: () => api.get('/dashboard/rfq-statistics')
};

// ================== BUDGET API ==================
export const budgetApi = {
    listFiscalYears: () => api.get('/budgets/fiscal-years'),
    getActiveFiscalYear: () => api.get('/budgets/fiscal-years/active'),
    createFiscalYear: (data) => api.post('/budgets/fiscal-years', data),
    activateFiscalYear: (id) => api.put(`/budgets/fiscal-years/${id}/activate`),
    list: (params) => api.get('/budgets', { params }),
    getMyDepartment: () => api.get('/budgets/my-department'),
    getById: (id) => api.get(`/budgets/${id}`),
    create: (data) => api.post('/budgets', data),
    submit: (id) => api.post(`/budgets/${id}/submit`),
    approve: (id, data) => api.post(`/budgets/${id}/approve`, data),
    activate: (id) => api.post(`/budgets/${id}/activate`),
    close: (id) => api.post(`/budgets/${id}/close`),
    checkAvailability: (id, data) => api.post(`/budgets/${id}/check-availability`, data),
    getUtilization: (id) => api.get(`/budgets/${id}/utilization`),
    requestTransfer: (data) => api.post('/budgets/transfers', data),
    approveTransfer: (id) => api.post(`/budgets/transfers/${id}/approve`),
    rejectTransfer: (id) => api.post(`/budgets/transfers/${id}/reject`)
};

// ================== CATALOG API ==================
export const catalogApi = {
    listCategories: (params) => api.get('/catalog/categories', { params }),
    getCategoryTree: () => api.get('/catalog/categories/tree'),
    getCategoryById: (id) => api.get(`/catalog/categories/${id}`),
    createCategory: (data) => api.post('/catalog/categories', data),
    updateCategory: (id, data) => api.put(`/catalog/categories/${id}`, data),
    listUOM: () => api.get('/catalog/uom'),
    createUOM: (data) => api.post('/catalog/uom', data),
    listItems: (params) => api.get('/catalog/items', { params }),
    searchItems: (params) => api.get('/catalog/items/search', { params }),
    getItemByCode: (code) => api.get(`/catalog/items/code/${code}`),
    getItemById: (id) => api.get(`/catalog/items/${id}`),
    createItem: (data) => api.post('/catalog/items', data),
    updateItem: (id, data) => api.put(`/catalog/items/${id}`, data),
    deleteItem: (id) => api.delete(`/catalog/items/${id}`)
};

// ================== PURCHASE REQUEST API ==================
export const purchaseRequestApi = {
    list: (params) => api.get('/purchase-requests', { params }),
    getMyRequests: (params) => api.get('/purchase-requests/my-requests', { params }),
    getPendingApprovals: () => api.get('/purchase-requests/pending-approvals'),
    getStatistics: () => api.get('/purchase-requests/statistics'),
    getByNumber: (prNumber) => api.get(`/purchase-requests/number/${prNumber}`),
    get: (id) => api.get(`/purchase-requests/${id}`),  // alias for getById
    getById: (id) => api.get(`/purchase-requests/${id}`),
    create: (data) => api.post('/purchase-requests', data),
    update: (id, data) => api.put(`/purchase-requests/${id}`, data),
    submit: (id) => api.post(`/purchase-requests/${id}/submit`),
    approve: (id, data) => api.post(`/purchase-requests/${id}/approve`, data),
    reject: (id, data) => api.post(`/purchase-requests/${id}/reject`, data),
    cancel: (id, data) => api.post(`/purchase-requests/${id}/cancel`, data)
};

// ================== VENDOR API ==================
export const vendorApi = {
    register: (data) => api.post('/vendors/register', data),
    list: (params) => api.get('/vendors', { params }),
    search: (params) => api.get('/vendors/search', { params }),
    getStatistics: () => api.get('/vendors/statistics'),
    getById: (id) => api.get(`/vendors/${id}`),
    create: (data) => api.post('/vendors', data),
    update: (id, data) => api.put(`/vendors/${id}`, data),
    submit: (id) => api.post(`/vendors/${id}/submit`),
    verify: (id) => api.post(`/vendors/${id}/verify`),
    approve: (id) => api.post(`/vendors/${id}/approve`),
    suspend: (id, data) => api.post(`/vendors/${id}/suspend`, data),
    blacklist: (id, data) => api.post(`/vendors/${id}/blacklist`, data),
    reactivate: (id) => api.post(`/vendors/${id}/reactivate`),
    addRating: (id, data) => api.post(`/vendors/${id}/ratings`, data),
    addBankAccount: (id, data) => api.post(`/vendors/${id}/bank-accounts`, data)
};

// ================== RFQ API ==================
export const rfqApi = {
    list: (params) => api.get('/rfq', { params }),
    get: (id) => api.get(`/rfq/${id}`),  // alias for getById
    getById: (id) => api.get(`/rfq/${id}`),
    create: (data) => api.post('/rfq', data),
    createFromPR: (prId, data) => api.post(`/rfq/from-pr/${prId}`, data),
    publish: (id) => api.post(`/rfq/${id}/publish`),
    close: (id) => api.post(`/rfq/${id}/close`),
    inviteVendor: (id, data) => api.post(`/rfq/${id}/invite-vendor`, data),
    compareQuotations: (id) => api.get(`/rfq/${id}/compare`),
    award: (id, data) => api.post(`/rfq/${id}/award`, data),
    submitQuotation: (rfqId, data) => api.post(`/rfq/${rfqId}/quotations`, data),
    getQuotation: (rfqId, quotationId) => api.get(`/rfq/${rfqId}/quotations/${quotationId}`),
    evaluateQuotation: (rfqId, quotationId, data) => api.post(`/rfq/${rfqId}/quotations/${quotationId}/evaluate`, data)
};

// ================== CONTRACT API ==================
export const contractApi = {
    list: (params) => api.get('/contracts', { params }),
    getExpiring: () => api.get('/contracts/expiring'),
    getById: (id) => api.get(`/contracts/${id}`),
    create: (data) => api.post('/contracts', data),
    createFromRFQ: (rfqId, data) => api.post(`/contracts/from-rfq/${rfqId}`, data),
    approve: (id) => api.post(`/contracts/${id}/approve`),
    activate: (id) => api.post(`/contracts/${id}/activate`),
    terminate: (id, data) => api.post(`/contracts/${id}/terminate`, data),
    createAmendment: (id, data) => api.post(`/contracts/${id}/amendments`, data),
    approveAmendment: (id, amendmentId) => api.post(`/contracts/${id}/amendments/${amendmentId}/approve`),
    completeMilestone: (id, milestoneId) => api.post(`/contracts/${id}/milestones/${milestoneId}/complete`)
};

// ================== PURCHASE ORDER API ==================
export const purchaseOrderApi = {
    list: (params) => api.get('/purchase-orders', { params }),
    getStatistics: () => api.get('/purchase-orders/statistics'),
    getById: (id) => api.get(`/purchase-orders/${id}`),
    create: (data) => api.post('/purchase-orders', data),
    createFromQuotation: (quotationId, data) => api.post(`/purchase-orders/from-quotation/${quotationId}`, data),
    submit: (id) => api.post(`/purchase-orders/${id}/submit`),
    approve: (id) => api.post(`/purchase-orders/${id}/approve`),
    send: (id, data) => api.post(`/purchase-orders/${id}/send`, data),
    acknowledge: (id, data) => api.post(`/purchase-orders/${id}/acknowledge`, data),
    cancel: (id, data) => api.post(`/purchase-orders/${id}/cancel`, data)
};

// ================== GOODS RECEIPT API ==================
export const goodsReceiptApi = {
    list: (params) => api.get('/goods-receipts', { params }),
    getPending: (poId) => api.get(`/goods-receipts/pending/${poId}`),
    getById: (id) => api.get(`/goods-receipts/${id}`),
    create: (data) => api.post('/goods-receipts', data),
    inspect: (id, data) => api.post(`/goods-receipts/${id}/inspect`, data),
    accept: (id) => api.post(`/goods-receipts/${id}/accept`),
    reject: (id, data) => api.post(`/goods-receipts/${id}/reject`, data)
};

// ================== INVOICE API ==================
export const invoiceApi = {
    list: (params) => api.get('/invoices', { params }),
    getById: (id) => api.get(`/invoices/${id}`),
    create: (data) => api.post('/invoices', data),
    verify: (id) => api.post(`/invoices/${id}/verify`),
    approve: (id) => api.post(`/invoices/${id}/approve`)
};

// ================== PAYMENT API ==================
export const paymentApi = {
    list: (params) => api.get('/payments', { params }),
    getById: (id) => api.get(`/payments/${id}`),
    create: (data) => api.post('/payments', data),
    approve: (id) => api.post(`/payments/${id}/approve`),
    complete: (id) => api.post(`/payments/${id}/complete`)
};

// ================== NOTIFICATION API ==================
export const notificationApi = {
    list: (params) => api.get('/notifications', { params }),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markAllAsRead: () => api.post('/notifications/mark-all-read'),
    markAsRead: (id) => api.post(`/notifications/${id}/read`),
    delete: (id) => api.delete(`/notifications/${id}`)
};

// ================== FILE API ==================
export const fileApi = {
    upload: (formData) => api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getAttachments: (entityType, entityId) => api.get(`/files/entity/${entityType}/${entityId}`),
    getById: (id) => api.get(`/files/${id}`),
    download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
    verify: (id) => api.get(`/files/${id}/verify`),
    delete: (id) => api.delete(`/files/${id}`)
};

// ================== REPORT API ==================
export const reportApi = {
    getPurchaseOrders: (params) => api.get('/reports/purchase-orders', { params }),
    getVendorPerformance: (params) => api.get('/reports/vendor-performance', { params }),
    getInvoiceAging: (params) => api.get('/reports/invoice-aging', { params }),
    getContractExpiry: (params) => api.get('/reports/contract-expiry', { params }),
    getSavings: (params) => api.get('/reports/savings', { params }),
    getCycleTime: (params) => api.get('/reports/cycle-time', { params })
};

// ================== VENDOR PORTAL API ==================
export const vendorPortalApi = {
    getStats: () => api.get('/vendor-portal/stats'),
    getPendingRFQs: () => api.get('/vendor-portal/pending-rfqs'),
    getRecentPOs: () => api.get('/vendor-portal/recent-pos'),
    getProfile: () => api.get('/vendor-portal/profile'),
    updateProfile: (data) => api.put('/vendor-portal/profile', data),
    submitQuotation: (rfqId, data) => api.post(`/vendor-portal/rfq/${rfqId}/quotation`, data),
    acknowledgePO: (poId) => api.post(`/vendor-portal/po/${poId}/acknowledge`),
    submitInvoice: (data) => api.post('/vendor-portal/invoices', data)
};

// ================== ROLE API ==================
export const roleApi = {
    list: (params) => api.get('/roles', { params }),
    getById: (id) => api.get(`/roles/${id}`),
    create: (data) => api.post('/roles', data),
    update: (id, data) => api.put(`/roles/${id}`, data),
    delete: (id) => api.delete(`/roles/${id}`),
    getPermissions: (id) => api.get(`/roles/${id}/permissions`),
    getAllPermissions: () => api.get('/roles/permissions'),
    updatePermissions: (id, data) => api.put(`/roles/${id}/permissions`, data)
};

// ================== DEPARTMENT API ==================
export const departmentApi = {
    list: (params) => api.get('/departments', { params }),
    getTree: () => api.get('/departments/tree'),
    getById: (id) => api.get(`/departments/${id}`),
    create: (data) => api.post('/departments', data),
    update: (id, data) => api.put(`/departments/${id}`, data),
    delete: (id) => api.delete(`/departments/${id}`)
};

// ================== AUDIT LOG API ==================
export const auditLogApi = {
    list: (params) => api.get('/audit-logs', { params }),
    getById: (id) => api.get(`/audit-logs/${id}`),
    getStatistics: (params) => api.get('/audit-logs/statistics', { params }),
    getEntityHistory: (entityType, entityId) => api.get(`/audit-logs/entity/${entityType}/${entityId}`)
};

// ================== COMPLIANCE API ==================
export const complianceApi = {
    getOverview: () => api.get('/compliance/overview'),
    listPolicies: (params) => api.get('/compliance/policies', { params }),
    getPolicyById: (id) => api.get(`/compliance/policies/${id}`),
    listChecks: (params) => api.get('/compliance/checks', { params }),
    runAudit: (data) => api.post('/compliance/audit', data)
};

export default api;
